// Browser/device notification utilities for new messages

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

export class NotificationService {

  static async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) return false;
    if (Notification.permission === 'granted') {
      await NotificationService.subscribeToPush();
      return true;
    }
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        await NotificationService.subscribeToPush();
        return true;
      }
    }
    return false;
  }

  // Subscribe to Web Push and send the subscription to the backend
  static async subscribeToPush(): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.ready;

      // Check if already subscribed
      const existing = await reg.pushManager.getSubscription();
      if (existing) return;

      // Fetch VAPID public key
      const res = await fetch(`${API_BASE_URL}/api/messages/push-vapid-key`, { credentials: 'include' });
      if (!res.ok) return;
      const { publicKey } = await res.json();
      if (!publicKey) return;

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      await fetch(`${API_BASE_URL}/api/messages/push-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(subscription)
      });
    } catch (err) {
      console.warn('[Push] Subscribe failed:', err);
    }
  }

  static async unsubscribeFromPush(): Promise<void> {
    if (!('serviceWorker' in navigator)) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;
      await fetch(`${API_BASE_URL}/api/messages/push-subscribe`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ endpoint: sub.endpoint })
      });
      await sub.unsubscribe();
    } catch (err) {
      console.warn('[Push] Unsubscribe failed:', err);
    }
  }

  static isEnabled(): boolean {
    if (!('Notification' in window)) return false;
    if (Notification.permission !== 'granted') return false;
    return localStorage.getItem('notificationsDisabled') !== 'true';
  }

  static async enable(): Promise<boolean> {
    localStorage.removeItem('notificationsDisabled');
    return NotificationService.requestPermission();
  }

  static async disable(): Promise<void> {
    localStorage.setItem('notificationsDisabled', 'true');
    await NotificationService.unsubscribeFromPush();
  }

  static async showMessageNotification(
    senderName: string,
    messagePreview: string,
    senderId: string
  ): Promise<void> {
    if (!NotificationService.isEnabled()) return;

    // Skip if the app is visible and focused
    if (document.visibilityState === 'visible' && document.hasFocus()) return;

    const title = `New message from ${senderName}`;
    const options: NotificationOptions = {
      body: messagePreview.substring(0, 100),
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: `message-${senderId}`,
      renotify: true,
      data: {
        senderId,
        url: `/dashboard/messages?user=${senderId}`
      }
    };

    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.ready;
      reg.showNotification(title, options);
    } else {
      const n = new Notification(title, options);
      n.onclick = (e) => {
        e.preventDefault();
        window.focus();
        window.location.href = `/dashboard/messages?user=${senderId}`;
        n.close();
      };
    }
  }

  static isSupported(): boolean {
    return 'Notification' in window;
  }

  static getPermission(): NotificationPermission {
    return Notification.permission;
  }
}

// Convert base64url VAPID key to Uint8Array for PushManager.subscribe()
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(raw.split('').map(c => c.charCodeAt(0)));
}
